import { useState } from 'react'
import { useAbruf } from './api'
import { Angabe, Karte, Merkmal, Tabelle, Zustand } from './bausteine'
import { datum, zeitpunkt } from './formate'

/** Ein Datum der Mindestliste, wie es die Anwendung berechnet. */
interface Eintrag {
  datum: string
  nummer: number
  bezeichnung: string
  noetig: 'P' | 'B' | 'K'
  quelle: string
  verantwortlicher: string
  erwartetAus: string
  fundort: string
  objekt: string | null
  status: string
  wert: string | null
  hinweis: string | null
  eingegangenAm: string | null
  herkunft: string | null
  fristende: string | null
  eskalationAb: string | null
  fristbeleg: string | null
  anfrage: string | null
  anfrageVorgangsnummer: string | null
  klaerfallId: string | null
}

interface Bereitschaft {
  vertragsnummer: string
  maloId: string | null
  zuordnungsbeginn: string | null
  monat: string
  ab: string
  bis: string
  abrechnungsbereit: boolean
  bereitSeit: string | null
  messtechnik: string
  eintraege: Eintrag[]
}

/** Klartext und Farbe je Status - die Werte der Aufzaehlung Abrechnungsbereitschaftsauskunft.Status. */
const STATUS: Record<string, { text: string; art: 'neutral' | 'gut' | 'warnung' | 'fehler' | 'betont' }> = {
  VORHANDEN: { text: 'vorhanden', art: 'gut' },
  VORHANDEN_BEANSTANDET: { text: 'vorhanden, beanstandet', art: 'warnung' },
  ENTFAELLT: { text: 'entfällt', art: 'neutral' },
  AUSSTEHEND_NICHT_FAELLIG: { text: 'ausstehend, Frist läuft', art: 'neutral' },
  UEBERFAELLIG: { text: 'überfällig', art: 'warnung' },
  ANGEFRAGT: { text: 'angefragt', art: 'betont' },
  KLAERFALL: { text: 'Klärfall', art: 'fehler' },
}

/**
 * Das Merkmal "abrechnungsbereit" des Vertrags mit der Liste der Mindestdaten.
 *
 * Unabhaengig vom Vertragsstatus: IN_BELIEFERUNG haengt an der Zuordnung, dieses
 * Merkmal daran, ob die Stammdaten fuer die Abrechnung eines Monats reichen
 * (docs/abrechnungsbereit.md). Die Anwendung berechnet es bei jedem Abruf.
 */
export function Abrechnungsbereitschaft({ vertragsnummer }: { vertragsnummer: string }) {
  const [monat, setzeMonat] = useState('')
  const [alle, setzeAlle] = useState(false)
  const bereitschaft = useAbruf<Bereitschaft>(
    `/api/mako/abrechnungsbereitschaft/vertraege/${vertragsnummer}${monat ? `?monat=${monat}` : ''}`,
    [monat],
  )
  const b = bereitschaft.daten
  const hindernd = (e: Eintrag) =>
    e.noetig !== 'K' && !['VORHANDEN', 'VORHANDEN_BEANSTANDET', 'ENTFAELLT'].includes(e.status)
  const zeilen = (b?.eintraege ?? []).filter((e) => alle || hindernd(e) || e.status === 'VORHANDEN_BEANSTANDET')

  return (
    <Karte
      titel="Abrechnungsbereitschaft"
      handlung={
        <div className="feld" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <label htmlFor="abrechnungsmonat">Monat</label>
          <input
            id="abrechnungsmonat"
            type="month"
            value={monat || b?.monat || ''}
            onChange={(ereignis) => setzeMonat(ereignis.target.value)}
          />
        </div>
      }
    >
      <Zustand laeuft={bereitschaft.laeuft} fehler={bereitschaft.fehler}>
        {b && (
          <>
            <div className="angaben" style={{ marginBottom: 14 }}>
              <Angabe beschriftung="Abrechnungsbereit">
                <Merkmal
                  art={b.abrechnungsbereit ? 'gut' : 'warnung'}
                  titel="Reichen die Stammdaten, um den Monat abzurechnen? Der Vertragsstatus hängt davon nicht ab."
                >
                  {b.abrechnungsbereit ? 'ja' : 'nein'}
                </Merkmal>
              </Angabe>
              <Angabe beschriftung="Zeitraum">
                {datum(b.ab)} – {datum(b.bis)} (exklusiv)
              </Angabe>
              <Angabe beschriftung="Bereit seit">{zeitpunkt(b.bereitSeit)}</Angabe>
              <Angabe beschriftung="Zuordnungsbeginn">{datum(b.zuordnungsbeginn)}</Angabe>
              <Angabe beschriftung="Messtechnik">{b.messtechnik}</Angabe>
              <Angabe beschriftung="Fehlend">
                {b.eintraege.filter(hindernd).length} von {b.eintraege.length}
              </Angabe>
            </div>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
              <input type="checkbox" checked={alle} onChange={(e) => setzeAlle(e.target.checked)} />
              alle Daten der Mindestliste zeigen, nicht nur die fehlenden
            </label>
            {zeilen.length === 0 ? (
              <div className="leer">Es fehlt nichts.</div>
            ) : (
              <Tabelle
                spalten={[
                  { name: 'Nr.' },
                  { name: 'Datum' },
                  { name: 'Objekt' },
                  { name: 'Status' },
                  { name: 'Wert / Hinweis' },
                  { name: 'Quelle' },
                  { name: 'Fristende' },
                  { name: 'Eskalation' },
                ]}
              >
                {zeilen.map((e) => (
                  <tr key={`${e.datum}-${e.objekt}`}>
                    <td>
                      {e.nummer} {e.noetig}
                    </td>
                    <td title={e.fundort}>{e.bezeichnung}</td>
                    <td className="mono">{e.objekt ?? '—'}</td>
                    <td>
                      <Merkmal art={STATUS[e.status]?.art ?? 'neutral'} titel={e.hinweis ?? undefined}>
                        {STATUS[e.status]?.text ?? e.status}
                      </Merkmal>
                    </td>
                    <td>{e.wert ?? e.hinweis ?? '—'}</td>
                    <td title={`${e.verantwortlicher}${e.herkunft ? `, eingegangen aus ${e.herkunft}` : ''}`}>
                      {e.erwartetAus} ({e.verantwortlicher})
                    </td>
                    <td title={e.fristbeleg ?? undefined}>{zeitpunkt(e.fristende)}</td>
                    <td>
                      {e.anfrage
                        ? `${e.anfrage} ${e.anfrageVorgangsnummer ?? ''}`
                        : e.klaerfallId
                          ? 'Klärfall'
                          : '—'}
                    </td>
                  </tr>
                ))}
              </Tabelle>
            )}
          </>
        )}
      </Zustand>
    </Karte>
  )
}
